import { alpha, AppBar, Toolbar, Box, Typography, useTheme, IconButton, useMediaQuery } from "@mui/material";
import { Menu as MenuIcon } from "@mui/icons-material";
import ProfileMenuComponent from "./ProfileMenuComponent";
import LoggedInProfileMenuComponent from "./LoggedInProfileMenuComponent";
import { useAuth } from "../../auth/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ABOUT_ROUTE_PATH,
  CATEGORIES_ROUTE_PATH,
  CONTACT_ROUTE_PATH,
  EXPENSES_ROUTE_PATH,
  HOME_ROUTE_PATH,
  INCOMES_ROUTE_PATH,
  MONTHLY_REPORTS_ROUTE_PATH,
} from "../../utils/constants/AppConstants";

interface HeaderComponentProps {
  onMobileMenuToggle?: () => void;
  isMobile?: boolean;
}

const HeaderComponent = ({ onMobileMenuToggle, isMobile = false }: HeaderComponentProps) => {
  const theme = useTheme();
  const prefersMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const showMobileMenuTrigger = (isMobile || prefersMobile) && isAuthenticated;

  const routeTitleMap: Record<string, string> = {
    [HOME_ROUTE_PATH]: "Dashboard",
    [EXPENSES_ROUTE_PATH]: "Expenses",
    [INCOMES_ROUTE_PATH]: "Income",
    [CATEGORIES_ROUTE_PATH]: "Categories",
    [MONTHLY_REPORTS_ROUTE_PATH]: "Reports",
    [ABOUT_ROUTE_PATH]: "About",
    [CONTACT_ROUTE_PATH]: "Contact",
  };

  const currentPageLabel = routeTitleMap[location.pathname] || "PocketBalance";

  return (
    <AppBar 
      position="fixed" 
      elevation={0}
      sx={{
        backgroundColor: alpha(theme.palette.background.default, 0.78),
        boxShadow: '0 12px 30px rgba(2, 6, 23, 0.35)',
        backdropFilter: 'blur(10px)',
        zIndex: theme.zIndex.drawer + 1, // Ensure header is above sidebar
        height: { xs: '56px', sm: '72px' },
      }}
    >
      <Toolbar 
        sx={{ 
          minHeight: { xs: '56px', sm: '72px' },
          width: '100%',
          px: { xs: 1, sm: 2, md: 3 },
          justifyContent: 'space-between',
        }}
      >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            {/* Mobile Menu Button - Only show on mobile when authenticated */}
            {showMobileMenuTrigger && (
              <IconButton
                onClick={onMobileMenuToggle}
                sx={{
                  color: theme.palette.text.primary,
                  border: `1px solid ${alpha(theme.palette.text.secondary, 0.2)}`,
                  borderRadius: 2,
                  width: 38,
                  height: 38,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.15),
                    borderColor: alpha(theme.palette.primary.main, 0.35),
                  },
                }}
                aria-label="Open more menu"
              >
                <MenuIcon />
              </IconButton>
            )}
            
            <Box 
              onClick={() => navigate(HOME_ROUTE_PATH)}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: { xs: 1, sm: 2 },
                cursor: 'pointer',
                minWidth: 0,
                '&:hover': {
                  transform: 'translateY(-1px)',
                },
                transition: 'transform 0.2s ease',
              }}
            >
              <Box 
                component="img" 
                src="/favicon.svg" 
                alt="PocketBalance" 
                sx={{ height: { xs: 28, sm: 36 }, width: { xs: 28, sm: 36 } }} 
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    letterSpacing: '-0.5px',
                    fontSize: { xs: '1rem', sm: '1.35rem' },
                    lineHeight: 1.1,
                  }}
                >
                  PocketBalance
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: { xs: 'none', sm: 'block' },
                    color: alpha(theme.palette.text.secondary, 0.9),
                    letterSpacing: '0.02em',
                  }}
                >
                  {currentPageLabel}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isAuthenticated ? <LoggedInProfileMenuComponent /> : <ProfileMenuComponent />}
          </Box>
      </Toolbar>
    </AppBar>
  );
};

export default HeaderComponent;
